import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    marketing: true,
    personalization: true,
    analytics: true,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: true,
      preferences: {
        essentials: true,
        marketing: true,
        personalization: true,
        analytics: true,
      },
      timestamp: new Date().toISOString(),
    }));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleReject = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: false,
      preferences: {
        essentials: true,
        marketing: false,
        personalization: false,
        analytics: false,
      },
      timestamp: new Date().toISOString(),
    }));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: true,
      preferences: {
        essentials: true,
        ...preferences,
      },
      timestamp: new Date().toISOString(),
    }));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleRejectAll = () => {
    setPreferences({
      marketing: false,
      personalization: false,
      analytics: false,
    });
    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted: false,
      preferences: {
        essentials: true,
        marketing: false,
        personalization: false,
        analytics: false,
      },
      timestamp: new Date().toISOString(),
    }));
    setShowBanner(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    setPreferences({
      marketing: true,
      personalization: true,
      analytics: true,
    });
    handleAccept();
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner && !showPreferences) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && !showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-900 border-t border-gray-800 shadow-2xl shadow-black/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-gray-300 text-sm leading-relaxed">
                  By clicking "Accept", you agree to the storing of cookies on your device to enhance site navigation, analyze site usage, and assist in our marketing efforts.{' '}
                  <a href="/privacy" className="text-emerald-400 hover:text-emerald-300 underline">
                    View our Privacy Policy
                  </a>{' '}
                  for more information.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-md transition-colors"
                >
                  Preferences
                </button>
                <button
                  onClick={handleReject}
                  className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-md transition-colors"
                >
                  Reject
                </button>
                <button
                  onClick={handleAccept}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-400 rounded-md transition-colors"
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70"
            onClick={() => {
              setShowPreferences(false);
              setShowBanner(true);
            }}
          />
          
          {/* Modal */}
          <div className="relative bg-gray-900 border border-gray-800 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowPreferences(false);
                setShowBanner(true);
              }}
              className="absolute top-4 right-4 p-1 text-gray-500 hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              <h2 className="text-xl font-bold text-white mb-6">
                Manage Consent Preferences by Category
              </h2>

              <div className="space-y-6">
                {/* Essentials - Always Active */}
                <div className="pb-4 border-b border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">Essentials</h3>
                    <span className="text-sm font-medium text-emerald-400">Always active</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Necessary for the site to function. Always On.
                  </p>
                </div>

                {/* Marketing */}
                <div className="pb-4 border-b border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => togglePreference('marketing')}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900 cursor-pointer"
                      />
                      <span className="font-semibold text-white">Marketing</span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-400 ml-8">
                    Used for targeted advertising.
                  </p>
                </div>

                {/* Personalization */}
                <div className="pb-4 border-b border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.personalization}
                        onChange={() => togglePreference('personalization')}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900 cursor-pointer"
                      />
                      <span className="font-semibold text-white">Personalization</span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-400 ml-8">
                    Remembers your preferences and provides enhanced features.
                  </p>
                </div>

                {/* Analytics */}
                <div className="pb-4 border-b border-gray-800">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => togglePreference('analytics')}
                        className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-gray-900 cursor-pointer"
                      />
                      <span className="font-semibold text-white">Analytics</span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-400 ml-8">
                    Measures usage and improves your experience.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-md transition-colors"
                >
                  Reject All
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-md transition-colors"
                >
                  Accept All
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-400 rounded-md transition-colors"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
